import { Fragment, useState } from "react";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { formatNumber, numberWithCommas } from "../../utils";
import {
  QrBranchSummary,
  QrChannelSummary,
} from "../../types/qr-analytics.type";
import { CASH_CHANNEL, DIGITAL_CHANNELS, DIGITAL_GROUP } from "./channel";

interface BranchTableProps {
  readonly branches: QrBranchSummary[];
  readonly previousLabel: string;
}

/** Columns per row: expander, สาขา, บิล, เงินสด, 3 group columns, %, ผลต่าง, ร้าน. */
const COLUMN_COUNT = 10;

/** The เงินโอน+QR columns sit on a faint band so the group reads as one block down the page. */
const GROUP_BAND = "#F7F7F9";

/**
 * Per-branch เงินโอน+QR adoption, expandable to the สาย breakdown underneath it.
 *
 * The header groups รวม / เงินโอน / QR under one เงินโอน+QR span, matching how the owner reads
 * the split everywhere else on the screen; the %, ผลต่าง and ร้าน columns are the group's.
 *
 * This is the screen's answer to the spreadsheet it replaces, which laid branches down the
 * page and routes across it. A wide matrix was rejected: branches carry between 6 and 15
 * routes, so the grid is mostly empty cells, and an empty cell and a 0% cell are different
 * facts that a matrix renders identically. Rows that expand keep both readings honest — a
 * route with no bills simply is not listed.
 */
export default function BranchTable({
  branches,
  previousLabel,
}: BranchTableProps) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const toggle = (companyId: string) =>
    setExpanded((current) => ({
      ...current,
      [companyId]: !current[companyId],
    }));

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle2" fontWeight={700} mb={1}>
        สัดส่วน {DIGITAL_GROUP.label} รายสาขา
      </Typography>
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={40} rowSpan={2} />
              <TableCell rowSpan={2}>สาขา</TableCell>
              <TableCell align="right" rowSpan={2}>
                บิลจากแอป
              </TableCell>
              <TableCell align="right" rowSpan={2}>
                {CASH_CHANNEL.label}
              </TableCell>
              <TableCell
                align="center"
                colSpan={3}
                sx={{
                  bgcolor: GROUP_BAND,
                  color: DIGITAL_GROUP.color,
                  fontWeight: 700,
                  borderBottom: `1.5px solid ${DIGITAL_GROUP.color}`,
                }}
              >
                {DIGITAL_GROUP.label}
              </TableCell>
              <TableCell align="right" rowSpan={2}>
                % {DIGITAL_GROUP.label}
              </TableCell>
              <TableCell align="right" rowSpan={2}>
                ผลต่าง{previousLabel}
              </TableCell>
              <TableCell align="right" rowSpan={2}>
                ร้านที่ใช้โอน/QR
              </TableCell>
            </TableRow>
            <TableRow>
              <GroupHeadCells />
            </TableRow>
          </TableHead>
          <TableBody>
            {branches.length === 0 && (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center">
                  <Typography variant="body2" color="text.secondary" py={2}>
                    ไม่มีข้อมูลในช่วงที่เลือก
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {branches.map((branch) => {
              const isOpen = Boolean(expanded[branch.company_id]);
              return (
                <Fragment key={branch.company_id}>
                  <TableRow hover>
                    <TableCell>
                      {branch.routes.length > 0 && (
                        <IconButton
                          size="small"
                          onClick={() => toggle(branch.company_id)}
                          aria-label={isOpen ? "ซ่อนสาย" : "ดูรายสาย"}
                        >
                          {isOpen ? (
                            <KeyboardArrowDownIcon fontSize="small" />
                          ) : (
                            <KeyboardArrowRightIcon fontSize="small" />
                          )}
                        </IconButton>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {branch.branch_name ?? branch.branch_code ?? "-"}
                      </Typography>
                      {branch.branch_code && branch.branch_name && (
                        <Typography variant="caption" color="text.secondary">
                          {branch.branch_code}
                        </Typography>
                      )}
                    </TableCell>
                    <ChannelCells row={branch} emphasis />
                    <TableCell align="right">
                      <DeltaText value={branch.digital_percent_delta} />
                    </TableCell>
                    <TableCell align="right">
                      {numberWithCommas(branch.digital_shop_count)}/
                      {numberWithCommas(branch.shop_count)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      colSpan={COLUMN_COUNT}
                      sx={{ p: 0, borderBottom: isOpen ? undefined : "none" }}
                    >
                      <Collapse in={isOpen} timeout="auto" unmountOnExit>
                        <Box sx={{ bgcolor: "#FAFAFB", px: 2, py: 1 }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>สาย</TableCell>
                                <TableCell align="right">บิลจากแอป</TableCell>
                                <TableCell align="right">
                                  {CASH_CHANNEL.label}
                                </TableCell>
                                <GroupHeadCells short />
                                <TableCell align="right">
                                  % {DIGITAL_GROUP.label}
                                </TableCell>
                                <TableCell align="right">
                                  ร้านที่ใช้โอน/QR
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {branch.routes.map((route) => (
                                <TableRow
                                  key={`${branch.company_id}-${route.route_code ?? "none"}`}
                                >
                                  <TableCell>
                                    {route.route_code ?? "ไม่ระบุสาย"}
                                  </TableCell>
                                  <ChannelCells row={route} />
                                  <TableCell align="right">
                                    {numberWithCommas(route.digital_shop_count)}
                                    /{numberWithCommas(route.shop_count)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

/** รวม / เงินโอน / QR under the group span — the route sub-table has no span, so it says โอน+QR. */
function GroupHeadCells({ short = false }: { readonly short?: boolean }) {
  return (
    <>
      <TableCell align="right" sx={{ bgcolor: GROUP_BAND }}>
        {short ? "โอน+QR" : "รวม"}
      </TableCell>
      {DIGITAL_CHANNELS.map((channel) => (
        <TableCell align="right" key={channel.key} sx={{ bgcolor: GROUP_BAND }}>
          {channel.label}
        </TableCell>
      ))}
    </>
  );
}

/**
 * บิลจากแอป, เงินสด, the three group columns and the group % — identical on a branch row and
 * a route row, so one component keeps the two tables from drifting apart.
 */
function ChannelCells({
  row,
  emphasis = false,
}: {
  readonly row: QrChannelSummary;
  readonly emphasis?: boolean;
}) {
  return (
    <>
      <TableCell align="right">{numberWithCommas(row.bill_count)}</TableCell>
      <TableCell align="right">{numberWithCommas(row.cash_count)}</TableCell>
      <TableCell align="right" sx={{ bgcolor: GROUP_BAND, fontWeight: 700 }}>
        {numberWithCommas(row.digital_count)}
      </TableCell>
      {DIGITAL_CHANNELS.map((channel) => (
        <TableCell align="right" key={channel.key} sx={{ bgcolor: GROUP_BAND }}>
          {numberWithCommas(row[channel.countKey] as number)}
        </TableCell>
      ))}
      <TableCell align="right">
        <Box display="inline-flex" alignItems="center" gap={1}>
          <MiniShareBar row={row} />
          <Typography
            variant="body2"
            fontWeight={emphasis ? 700 : 600}
            sx={{ color: DIGITAL_GROUP.color }}
          >
            {formatNumber(row.digital_percent)}%
          </Typography>
        </Box>
      </TableCell>
    </>
  );
}

/**
 * The group % as a bar of the whole bill population, split into its two members, so a row
 * where QR carries the group and one where เงินโอน does look different at a glance.
 */
function MiniShareBar({ row }: { readonly row: QrChannelSummary }) {
  return (
    <Box
      aria-hidden
      sx={{
        display: "inline-flex",
        gap: "1px",
        width: 64,
        height: 8,
        bgcolor: "#EEF0F3",
        borderRadius: "2px",
        overflow: "hidden",
      }}
    >
      {DIGITAL_CHANNELS.map((channel) => (
        <Box
          key={channel.key}
          sx={{
            width: `${row[channel.percentKey] as number}%`,
            bgcolor: channel.color,
          }}
        />
      ))}
    </Box>
  );
}

/**
 * The ผลต่าง cell. `null` means the branch sold nothing in the previous window, which is not
 * the same as "no change" — it prints an em dash rather than a green 0%.
 */
function DeltaText({ value }: { readonly value: number | null }) {
  if (value === null) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }
  const color = value > 0 ? "#2E7D32" : value < 0 ? "#C62828" : undefined;
  return (
    <Typography variant="body2" fontWeight={600} sx={{ color }}>
      {value > 0 ? "+" : ""}
      {formatNumber(value)}%
    </Typography>
  );
}
